import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserDetails } from '../../../redux/userRelated/userHandle';
import { getStudentFee } from '../../../redux/feeRelated/feeHandle';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Button, Tab, Container, Typography, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Chip, Grid,
    Card, CardContent, MenuItem, Select, FormControl,
    InputLabel, CircularProgress, Alert, TextField,
    IconButton, Avatar, Divider
} from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import CustomPieChart from '../../../components/CustomPieChart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import axios from 'axios';
import { formatNepaliDate } from '../../../utils/nepaliDate';
import { formatDatePart, getNepaliMonthOptions, getMonthNameFromDate } from '../../../utils/customDate';
import styled, { keyframes } from 'styled-components';

const ViewStudent = () => {
    const [value, setValue] = useState('1');
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch();

    const studentID = params.id;
    const address = "Student";

    const { userDetails, loading: studentLoading, error: studentError } = useSelector((state) => state.user);
    const { feesList: feeData, loading: feeLoading } = useSelector((state) => state.fee);
    const { currentUser } = useSelector(state => state.user);

    const [marks, setMarks] = useState([]);
    const [marksLoading, setMarksLoading] = useState(false);
    const [marksError, setMarksError] = useState('');
    const [marksExamTypeFilter, setMarksExamTypeFilter] = useState('');
    const [marksSubjectFilter, setMarksSubjectFilter] = useState('');
    const [marksMonthFilter, setMarksMonthFilter] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [idCardOpen, setIdCardOpen] = useState(false);

    useEffect(() => {
        if (studentID) {
            dispatch(getUserDetails(studentID, address));
            dispatch(getStudentFee(studentID));
            fetchStudentMarks();
        }
    }, [dispatch, studentID]);

    const fetchStudentMarks = async () => {
        setMarksLoading(true);
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/Marks/Student/${studentID}`);
            if (result.data && result.data.message) {
                setMarks([]);
            } else if (Array.isArray(result.data)) {
                setMarks(result.data);
            }
        } catch (error) {
            console.error('Error fetching marks:', error);
            setMarksError('Error loading marks data');
        }
        setMarksLoading(false);
    };

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const calculateAttendanceStats = () => {
        if (!userDetails?.attendance || userDetails.attendance.length === 0) {
            return { present: 0, absent: 0, total: 0, percentage: 0 };
        }
        const present = userDetails.attendance.filter(a => a.status === 'Present').length;
        const absent = userDetails.attendance.filter(a => a.status === 'Absent').length;
        const total = userDetails.attendance.length;
        const percentage = total > 0 ? Math.round((present / total) * 100) : 0;
        return { present, absent, total, percentage };
    };

    const attendanceStats = calculateAttendanceStats();

    const getAttendanceChartData = () => {
        if (attendanceStats.total === 0) return [];
        return [
            { name: 'Present', value: attendanceStats.present },
            { name: 'Absent', value: attendanceStats.absent }
        ];
    };

    const getFilteredAttendance = () => {
        if (!userDetails?.attendance) return [];
        if (!selectedMonth) return userDetails.attendance;
        return userDetails.attendance.filter(a => {
            if (!a.date || typeof a.date !== 'string') return false;
            const parts = a.date.split('-');
            const filterParts = selectedMonth.split('-');
            return parts[0] === filterParts[0] && parts[1] === filterParts[1];
        });
    };

    const getFilteredMarks = () => {
        if (!marks) return [];
        return marks.filter(mark => {
            const subjectObj = mark.subject || mark.subName || {};
            const subjectName = subjectObj.subName || (typeof mark.subject === 'string' ? mark.subject : '');

            const matchExam = marksExamTypeFilter ? mark.examType === marksExamTypeFilter : true;
            const matchSubject = marksSubjectFilter ? subjectName.toLowerCase().includes(marksSubjectFilter.toLowerCase()) : true;
            
            let matchMonth = true;
            if (marksMonthFilter && mark.examDate) {
                const markParts = mark.examDate.split('-');
                const filterParts = marksMonthFilter.split('-');
                matchMonth = markParts[0] === filterParts[0] && markParts[1] === filterParts[1];
            }

            return matchExam && matchSubject && matchMonth;
        });
    };

    const calculateMarksStats = (marksToCalc) => {
        if (!marksToCalc || marksToCalc.length === 0) {
            return { totalExams: 0, averagePercentage: 0, totalMarks: 0, obtainedMarks: 0 };
        }
        let totalMarks = 0;
        let obtainedMarks = 0;
        marksToCalc.forEach(mark => {
            totalMarks += mark.maxMarks || 0;
            obtainedMarks += mark.marksObtained || 0;
        });
        const averagePercentage = totalMarks > 0 ? ((obtainedMarks / totalMarks) * 100).toFixed(1) : 0;
        return { totalExams: marksToCalc.length, averagePercentage, totalMarks, obtainedMarks };
    };

    const marksStats = calculateMarksStats(getFilteredMarks());

    const getGradeColor = (percentage) => {
        const percent = parseFloat(percentage);
        if (percent >= 90) return 'success';
        if (percent >= 80) return 'primary';
        if (percent >= 70) return 'info';
        if (percent >= 60) return 'warning';
        return 'error';
    };

    const calculateFeeStats = () => {
        if (!feeData || !Array.isArray(feeData) || feeData.length === 0) {
            return { total: 0, paid: 0, unpaid: 0, partial: 0 };
        }
        let totalExpected = 0, totalPaid = 0, totalPartial = 0;
        const feeDetails = feeData[0]?.feeDetails || feeData;
        if (Array.isArray(feeDetails)) {
            feeDetails.forEach(fee => {
                totalExpected += (fee.monthlyAmount || 0);
                totalPaid += (fee.paidAmount || 0);
                if (fee.status === 'Partial') {
                    totalPartial += (fee.paidAmount || 0);
                }
            });
        }
        return { 
            total: totalExpected, 
            paid: totalPaid, 
            unpaid: Math.max(0, totalExpected - totalPaid), 
            partial: totalPartial 
        };
    };

    const feeStats = calculateFeeStats();

    const getStatusColor = (status) => {
        switch (status) {
            case 'Paid': return 'success';
            case 'Unpaid': return 'error';
            case 'Partial': return 'warning';
            default: return 'default';
        }
    };

    const getUniqueMonths = () => {
        if (!userDetails?.attendance) return [];
        const months = new Set();
        userDetails.attendance.forEach(a => {
            if (a.date && typeof a.date === 'string') {
                const parts = a.date.split('-');
                if (parts.length >= 2) {
                    months.add(`${parts[0]}-${parts[1]}`);
                }
            }
        });
        return Array.from(months).sort().reverse();
    };



    if (studentLoading || feeLoading) {
        return (
            <PageContainer>
                <LoadingBox>
                    <CircularProgress size={60} />
                    <Typography sx={{ mt: 2 }}>Loading student details...</Typography>
                </LoadingBox>
            </PageContainer>
        );
    }

    if (studentError) {
        return (
            <PageContainer>
                <MainContent>
                    <Alert severity="error">Error fetching student details: {studentError}</Alert>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
                        Go Back
                    </Button>
                </MainContent>
            </PageContainer>
        );
    }


    return (
        <PageContainer>
            <StickyHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate(-1)} sx={{ color: 'white' }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Student Profile
                    </Typography>
                </Box>

            </StickyHeader>

            <MainContent maxWidth="xl">
                <Grid container spacing={4}>
                    <Grid item xs={12} md={4}>
                        <InfoCard elevation={3}>
                            <ProfileHeader>
                                <Avatar
                                    src={userDetails?.photo ? `${process.env.REACT_APP_BASE_URL}/${userDetails.photo}` : ""}
                                    sx={{ width: 120, height: 120, mb: 2, border: '4px solid white', boxShadow: 2 }}
                                >
                                    {userDetails?.name?.charAt(0)}
                                </Avatar>
                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                                    {userDetails?.name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                    Roll No: {userDetails?.rollNum || 'N/A'} | Class: {userDetails?.sclassName?.sclassName || 'N/A'}
                                </Typography>
                            </ProfileHeader>
                            <Divider sx={{ my: 2 }} />
                            <ContactInfo>
                                <InfoItem>
                                    <EmailIcon color="primary" fontSize="small" />
                                    <Typography variant="body2">{userDetails?.email || 'No email provided'}</Typography>
                                </InfoItem>
                                <InfoItem>
                                    <PhoneIcon color="primary" fontSize="small" />
                                    <Typography variant="body2">{userDetails?.phone || 'No phone provided'}</Typography>
                                </InfoItem>
                                <InfoItem>
                                    <LocationOnIcon color="primary" fontSize="small" />
                                    <Typography variant="body2">{userDetails?.address || 'No address provided'}</Typography>
                                </InfoItem>
                            </ContactInfo>
                        </InfoCard>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <DetailsPaper elevation={3}>
                            <TabContext value={value}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                                    <TabList onChange={handleChange} variant="scrollable" scrollButtons="auto">
                                        <Tab label="Details" value="1" />
                                        <Tab label="Attendance" value="2" />
                                        <Tab label="Marks" value="3" />
                                        <Tab label="Fee" value="4" />
                                    </TabList>
                                </Box>

                                <TabPanel value="1" sx={{ px: 0 }}>
                                    <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>Academic Details</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom color="primary">Basic Information</Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" color="textSecondary">Name</Typography>
                                                            <Typography variant="body1" fontWeight="medium">{userDetails?.name || 'N/A'}</Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" color="textSecondary">Roll Number</Typography>
                                                            <Typography variant="body1" fontWeight="medium">{userDetails?.rollNum || 'N/A'}</Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" color="textSecondary">Class</Typography>
                                                            <Typography variant="body1" fontWeight="medium">{userDetails?.sclassName?.sclassName || 'N/A'}</Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" color="textSecondary">School</Typography>
                                                            <Typography variant="body1" fontWeight="medium">{userDetails?.school?.schoolName || 'N/A'}</Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" color="textSecondary">Email</Typography>
                                                            <Typography variant="body1" fontWeight="medium">{userDetails?.email || 'N/A'}</Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" color="textSecondary">Phone</Typography>
                                                            <Typography variant="body1" fontWeight="medium">{userDetails?.phone || 'N/A'}</Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" color="textSecondary">Date of Birth</Typography>
                                                            <Typography variant="body1" fontWeight="medium">{userDetails?.dob ? userDetails.dob.split('T')[0] : 'N/A'}</Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Typography variant="body2" color="textSecondary">Address</Typography>
                                                            <Typography variant="body1" fontWeight="medium">{userDetails?.address || 'N/A'}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom color="primary">Attendance Overview</Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={4}>
                                                            <Typography variant="h4" color="success.main">{attendanceStats.percentage}%</Typography>
                                                            <Typography variant="body2" color="textSecondary">Attendance Rate</Typography>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Typography variant="h4" color="success.main">{attendanceStats.present}</Typography>
                                                            <Typography variant="body2" color="textSecondary">Present Days</Typography>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Typography variant="h4" color="error.main">{attendanceStats.absent}</Typography>
                                                            <Typography variant="body2" color="textSecondary">Absent Days</Typography>
                                                        </Grid>
                                                    </Grid>
                                                    {getAttendanceChartData().length > 0 && (
                                                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                                            <CustomPieChart data={getAttendanceChartData()} />
                                                        </Box>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                </TabPanel>

                                <TabPanel value="2">
                                    <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>Attendance Details</Typography>
                                    <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={6} md={4}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Filter by Month</InputLabel>
                                                    <Select value={selectedMonth} label="Filter by Month" onChange={(e) => setSelectedMonth(e.target.value)}>
                                                        <MenuItem value="">All Months</MenuItem>
                                                        {getUniqueMonths().map(month => (
                                                            <MenuItem key={month} value={month}>{getMonthNameFromDate(month + '-01')} {month.split('-')[0]}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={8}>
                                                <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                                                    <Chip
                                                        icon={<CheckCircleIcon />}
                                                        label={`Present: ${getFilteredAttendance().filter(a => a.status === 'Present').length}`}
                                                        color="success"
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        icon={<CancelIcon />}
                                                        label={`Absent: ${getFilteredAttendance().filter(a => a.status === 'Absent').length}`}
                                                        color="error"
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table>
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {getFilteredAttendance().length === 0 ? (
                                                    <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4 }}>No attendance records found</TableCell></TableRow>
                                                ) : (getFilteredAttendance().map((attendance, index) => (
                                                    <TableRow key={index} hover>
                                                        <TableCell>{formatDatePart(attendance.date)}</TableCell>
                                                        <TableCell>
                                                            <Chip icon={attendance.status === 'Present' ? <CheckCircleIcon /> : <CancelIcon />} label={attendance.status} color={attendance.status === 'Present' ? 'success' : 'error'} size="small" />
                                                        </TableCell>
                                                    </TableRow>
                                                )))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </TabPanel>

                                <TabPanel value="3">
                                    <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                            Marks & Exam Results
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                                <InputLabel>Exam Type</InputLabel>
                                                <Select value={marksExamTypeFilter} label="Exam Type" onChange={(e) => setMarksExamTypeFilter(e.target.value)}>
                                                    <MenuItem value="">All Exams</MenuItem>
                                                    <MenuItem value="First Terminal">First Terminal</MenuItem>
                                                    <MenuItem value="Second Terminal">Second Terminal</MenuItem>
                                                    <MenuItem value="Mid-Terminal">Mid-Terminal</MenuItem>
                                                    <MenuItem value="Annual">Annual</MenuItem>
                                                    <MenuItem value="Test">Test</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                                <InputLabel>Month</InputLabel>
                                                <Select value={marksMonthFilter} label="Month" onChange={(e) => setMarksMonthFilter(e.target.value)}>
                                                    <MenuItem value="">All Months</MenuItem>
                                                    {Array.from(new Set(marks.map(m => m.examDate?.slice(0, 7)))).filter(Boolean).map(month => (
                                                        <MenuItem key={month} value={month}>{getMonthNameFromDate(month + '-01')} {month.split('-')[0]}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <TextField
                                                size="small"
                                                label="Search Subject"
                                                value={marksSubjectFilter}
                                                onChange={(e) => setMarksSubjectFilter(e.target.value)}
                                                sx={{ minWidth: 150 }}
                                            />
                                        </Box>
                                    </Box>

                                    {marksLoading ? (
                                        <Box sx={{ textAlign: 'center', py: 8 }}>
                                            <CircularProgress size={48} thickness={4} />
                                            <Typography sx={{ mt: 2, fontWeight: 600, color: '#64748b' }}>Loading marks data...</Typography>
                                        </Box>
                                    ) : marksError ? (
                                        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{marksError}</Alert>
                                    ) : (
                                        <>
                                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                                {[
                                                    { label: 'Filtered Exams', value: marksStats.totalExams, color: '#3b82f6', bgcolor: '#eff6ff' },
                                                    { label: 'Average', value: `${marksStats.averagePercentage}%`, color: '#10b981', bgcolor: '#ecfdf5' },
                                                    { label: 'Total Marks', value: marksStats.totalMarks, color: '#f59e0b', bgcolor: '#fffbeb' },
                                                    { label: 'Obtained', value: marksStats.obtainedMarks, color: '#8b5cf6', bgcolor: '#f5f3ff' }
                                                ].map((stat, i) => (
                                                    <Grid item xs={12} sm={6} md={3} key={i}>
                                                        <Paper elevation={0} sx={{ p: 3, textAlign: 'center', borderRadius: 4, bgcolor: stat.bgcolor, border: `1px solid ${stat.color}30` }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>
                                                                {stat.label}
                                                            </Typography>
                                                            <Typography variant="h4" sx={{ fontWeight: 900, color: stat.color, mt: 1 }}>
                                                                {stat.value}
                                                            </Typography>
                                                        </Paper>
                                                    </Grid>
                                                ))}
                                            </Grid>

                                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                            <TableCell sx={{ fontWeight: 800, color: '#475569', py: 2.5 }}>Date</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Exam Type</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Subject</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Teacher</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Score</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, color: '#475569' }}>%</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Grade</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {getFilteredMarks().length === 0 ? (
                                                            <TableRow>
                                                                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                                                    <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 600 }}>No marks records found</Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ) : (getFilteredMarks().map((mark) => {
                                                            const percentage = mark.maxMarks > 0 ? ((mark.marksObtained / mark.maxMarks) * 100).toFixed(1) : 0;
                                                            const subName = mark.subject?.subName || mark.subName?.subName ||
                                                                (typeof mark.subject === 'string' ? mark.subject :
                                                                    (mark.subject && typeof mark.subject.toString === 'function' ? mark.subject.toString() : 'N/A')
                                                                );

                                                            return (
                                                                <TableRow key={mark._id} hover sx={{ '& td': { borderBottom: '1px solid #f1f5f9' } }}>
                                                                    <TableCell>
                                                                        <Typography sx={{ fontWeight: 600, color: '#64748b' }}>
                                                                            {formatDatePart(mark.examDate)}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Chip label={mark.examType} sx={{ fontWeight: 700, bgcolor: '#e0e7ff', color: '#4338ca' }} size="small" />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography sx={{ fontWeight: 800, color: '#1e293b' }}>{subName}</Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography sx={{ color: '#475569', fontWeight: 500 }}>{mark.teacher?.name || 'N/A'}</Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography sx={{ fontWeight: 800, color: '#1e293b' }}>
                                                                            {mark.marksObtained} <Box component="span" sx={{ color: '#94a3b8', fontWeight: 500, fontSize: '0.875rem' }}>/ {mark.maxMarks}</Box>
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Chip
                                                                            label={`${percentage}%`}
                                                                            color={getGradeColor(percentage)}
                                                                            size="small"
                                                                            sx={{ fontWeight: 800, borderRadius: 1.5 }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Avatar sx={{
                                                                            width: 32,
                                                                            height: 32,
                                                                            bgcolor: percentage >= 80 ? '#10b981' : percentage >= 60 ? '#3b82f6' : percentage >= 40 ? '#f59e0b' : '#ef4444',
                                                                            fontWeight: 900,
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            {mark.grade || '-'}
                                                                        </Avatar>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        }))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </>
                                    )}
                                </TabPanel>

                                <TabPanel value="4">
                                    <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>Fee Details</Typography>
                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        <Grid item xs={12} sm={6} md={3}><Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}><Typography variant="h6">Total Fee</Typography><Typography variant="h4" color="primary.main">Rs.{feeStats.total.toFixed(2)}</Typography></Paper></Grid>
                                        <Grid item xs={12} sm={6} md={3}><Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}><Typography variant="h6">Paid</Typography><Typography variant="h4" color="success.main">Rs.{feeStats.paid.toFixed(2)}</Typography></Paper></Grid>
                                        <Grid item xs={12} sm={6} md={3}><Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee' }}><Typography variant="h6">Due</Typography><Typography variant="h4" color="error.main">Rs.{feeStats.unpaid.toFixed(2)}</Typography></Paper></Grid>
                                        <Grid item xs={12} sm={6} md={3}><Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}><Typography variant="h6">Partial</Typography><Typography variant="h4" color="warning.main">Rs.{feeStats.partial.toFixed(2)}</Typography></Paper></Grid>
                                    </Grid>
                                    <Paper sx={{ p: 2 }} variant="outlined">
                                        <Typography variant="h6" gutterBottom>Monthly Fee Breakdown</Typography>
                                        {feeData && Array.isArray(feeData) && feeData.length > 0 ? (
                                            <TableContainer>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Month</TableCell>
                                                            <TableCell align="right">Amount</TableCell>
                                                            <TableCell>Payment Date</TableCell>
                                                            <TableCell>Status</TableCell>
                                                            <TableCell>Description</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {(() => {
                                                            const feeDetails = feeData[0]?.feeDetails || feeData;
                                                            return feeDetails.map((fee, index) => (
                                                                <TableRow key={fee?._id || index}>
                                                                    <TableCell>{fee?.month}</TableCell>
                                                                    <TableCell align="right">Rs.{(fee?.amount || 0).toLocaleString()}</TableCell>
                                                                    <TableCell>{fee?.paymentDate ? formatDatePart(fee.paymentDate) : 'N/A'}</TableCell>
                                                                    <TableCell><Chip label={fee?.status || 'Unknown'} color={getStatusColor(fee?.status)} size="small" /></TableCell>
                                                                    <TableCell>
                                                                        {(() => {
                                                                            let desc = fee?.description || '';
                                                                            const dues = Number(fee?.duesAmount) || 0;
                                                                            const disc = Number(fee?.discount) || 0;
                                                                            if (dues > 0) {
                                                                                const duesText = `Dues: Rs. ${dues}`;
                                                                                if (!desc.toLowerCase().includes('dues') || !desc.includes(String(dues))) {
                                                                                    desc = desc ? `${desc}, - ${duesText}` : duesText;
                                                                                }
                                                                            }
                                                                            if (disc > 0) {
                                                                                const discText = `Discount: Rs. ${disc}`;
                                                                                if (!desc.toLowerCase().includes('discount') || !desc.includes(String(disc))) {
                                                                                    desc = desc ? `${desc}, - ${discText}` : discText;
                                                                                }
                                                                            }
                                                                            return desc || '-';
                                                                        })()}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ));
                                                        })()}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        ) : (<Typography sx={{ textAlign: 'center', py: 3 }}>No fee records found for this student.</Typography>)}
                                    </Paper>
                                </TabPanel>
                            </TabContext>
                        </DetailsPaper>
                    </Grid>
                </Grid>
            </MainContent>

        </PageContainer>
    );
};

export { ViewStudent };

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
    background-color: #f0f2f5;
    min-height: 100vh;
`;

const StickyHeader = styled(Box)`
    position: sticky;
    top: 0;
    z-index: 1000;
    background: linear-gradient(to right, #1a1a2e, #16213e);
    color: white;
    padding: 16px 40px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
`;

const MainContent = styled(Container)`
    padding-top: 40px;
    padding-bottom: 40px;
    animation: ${fadeIn} 0.6s ease-out;
`;

const InfoCard = styled(Paper)`
    padding: 32px;
    border-radius: 24px !important;
    text-align: center;
`;

const ProfileHeader = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ContactInfo = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 16px;
    text-align: left;
    margin-top: 20px;
`;

const InfoItem = styled(Box)`
    display: flex;
    align-items: center;
    gap: 12px;
    color: #444;
`;

const DetailsPaper = styled(Paper)`
    padding: 40px;
    border-radius: 24px !important;
    height: 100%;
`;

const LoadingBox = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 80vh;
    color: #667eea;
`;
